import { Button, Tooltip } from 'antd';

import {
  CameraOutlined,
  PrinterOutlined,
  SaveOutlined,
  TableOutlined,
} from '@ant-design/icons';

import { useStore } from '@project/components/hooks';
import { observer } from 'mobx-react';
import { Feature } from 'ol';
import { useCartogramStore } from '../context';
import { CartogramLegendSettings } from '../types';

import { download } from '@project/utils/file';
import { ReactNode } from 'react';
import './style.scss';

export type CartogramAction = 'print' | 'save' | 'image' | 'table';

type ActionSettings = { icon: ReactNode; title: string };

const cartogramActionSettings: Record<CartogramAction, ActionSettings> = {
  print: { icon: <PrinterOutlined />, title: 'Печать' },
  save: { icon: <SaveOutlined />, title: 'Скачать' },
  image: { icon: <CameraOutlined />, title: 'Скриншот' },
  table: { icon: <TableOutlined />, title: 'Таблица' },
};

export type CartogramActionHandler = (
  settings: CartogramLegendSettings[] | null,
  features: Feature[],
) => void;

export interface CartogramActionsProps {
  isDisabled?: boolean;
  className?: string;
}

export const CartogramActions = observer(function CartogramActions({
  isDisabled,
  className = '',
}: CartogramActionsProps) {
  const { cartogramsPageModal } = useStore();
  const {
    mapController,
    legendsSettings,
    filters,
    generatePdf,
    getCartogramScreenshot,
  } = useCartogramStore();

  const cartogramActionWrapper =
    (callback: CartogramActionHandler, validate: boolean = true) =>
    async () => {
      const foundFeatures = mapController.getFeatures();

      if (!validate && foundFeatures) {
        return callback(null, foundFeatures);
      } else {
        const settingsValues = await legendsSettings.form?.validateFields();

        if (!settingsValues || !foundFeatures) return;

        return callback(settingsValues.settings, foundFeatures);
      }
    };

  const handlePrint = async () => {
    const generatedCartogram = await generatePdf();
    generatedCartogram?.print();
  };

  const handleSave = async () => {
    const generatedCartogram = await generatePdf();
    generatedCartogram?.download();
  };

  const handleScreenshot = async () => {
    const screenshot = await getCartogramScreenshot();

    return download(screenshot, 'cartograms');
  };

  const handleOpenTable: CartogramActionHandler = (_, features) => {
    if (!filters.cartogramTable || !filters.tableColumn) return;

    cartogramsPageModal.open({
      cartogram:
        filters.cartogramTable.description ?? filters.cartogramTable.table_name,
      features,
      computeColumn: filters.tableColumn,
    });
  };

  const cartogramActionHandlers: Record<CartogramAction, () => Promise<void>> =
    {
      print: cartogramActionWrapper(handlePrint),
      save: cartogramActionWrapper(handleSave),
      image: handleScreenshot,
      table: cartogramActionWrapper(handleOpenTable, false),
    };

  return (
    <div className={`cartogram__action__list ${className}`}>
      {Object.entries(cartogramActionSettings).map(([action, settings]) => (
        <Tooltip key={action} title={settings.title}>
          <Button
            className="cartogram__action__list__button"
            disabled={isDisabled}
            icon={settings.icon}
            onClick={cartogramActionHandlers[action as CartogramAction]}
          />
        </Tooltip>
      ))}
    </div>
  );
});
